// Config panel for the "send_notification" action block
// Supports Email (via EmailJS templates) and Pushover channels
import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const EMAIL_TEMPLATES = [
  { value: 'system_notification', label: 'General Notification', description: 'Standard notification with title, message, and action button' },
  { value: 'alarm_notification', label: 'Alarm Notification', description: 'Alarm-style alert notification' },
  { value: 'system_systemAlert', label: 'System Alert', description: 'System-level alert for critical issues' },
  { value: 'device_offline', label: 'Device Offline', description: 'Device went offline notification' },
  { value: 'device_online', label: 'Device Online', description: 'Device came back online notification' },
  { value: 'site_outage', label: 'Site Outage', description: 'Full site outage alert' },
  { value: 'site_recovered', label: 'Site Recovered', description: 'Site recovered from outage' },
];

const EMAIL_TYPES = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'success', label: 'Success' },
];

const PUSHOVER_PRIORITIES = [
  { value: -2, label: 'Lowest (no alert)' },
  { value: -1, label: 'Low (quiet)' },
  { value: 0, label: 'Normal' },
  { value: 1, label: 'High' },
  { value: 2, label: 'Emergency (repeat until acknowledged)' },
];

const PUSHOVER_SOUNDS = [
  { value: 'pushover', label: 'Pushover (default)' },
  { value: 'bike', label: 'Bike' },
  { value: 'bugle', label: 'Bugle' },
  { value: 'cashregister', label: 'Cash Register' },
  { value: 'classical', label: 'Classical' },
  { value: 'cosmic', label: 'Cosmic' },
  { value: 'falling', label: 'Falling' },
  { value: 'gamelan', label: 'Gamelan' },
  { value: 'incoming', label: 'Incoming' },
  { value: 'intermission', label: 'Intermission' },
  { value: 'magic', label: 'Magic' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'pianobar', label: 'Piano Bar' },
  { value: 'siren', label: 'Siren' },
  { value: 'spacealarm', label: 'Space Alarm' },
  { value: 'tugboat', label: 'Tugboat' },
  { value: 'alien', label: 'Alien Alarm (long)' },
  { value: 'climb', label: 'Climb (long)' },
  { value: 'persistent', label: 'Persistent (long)' },
  { value: 'echo', label: 'Pushover Echo (long)' },
  { value: 'updown', label: 'Up Down (long)' },
  { value: 'vibrate', label: 'Vibrate Only' },
  { value: 'none', label: 'None (silent)' },
];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const SendNotificationConfig = ({ config, onChange, onSummaryChange }) => {
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState(null);

  const channels = config.channels || ['email'];
  const manualEmails = config.manualEmails || [];

  const updateSummary = (updated) => {
    const ch = (updated.channels || ['email']).join(' + ');
    const recip = updated.recipients === 'manual'
      ? `${(updated.manualEmails || []).length} email(s)`
      : 'location contacts';
    onSummaryChange(`${ch} to ${recip}`);
  };

  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    updateSummary(updated);
  };

  const handleChannelToggle = (channel) => {
    const current = config.channels || ['email'];
    let updated;
    if (current.includes(channel)) {
      updated = current.filter(c => c !== channel);
      if (updated.length === 0) return; // must have at least one
    } else {
      updated = [...current, channel];
    }
    handleChange('channels', updated);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setEmailError('Invalid email address');
      return;
    }
    if (manualEmails.includes(email)) {
      setEmailError('Email already added');
      return;
    }
    setEmailError(null);
    setEmailInput('');
    handleChange('manualEmails', [...manualEmails, email]);
  };

  const handleRemoveEmail = (email) => {
    handleChange('manualEmails', manualEmails.filter(e => e !== email));
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Sends a notification via email and/or Pushover when this block executes.
      </Typography>

      {/* --- Channel Selection --- */}
      <FormGroup>
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>Channels</Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={channels.includes('email')}
              onChange={() => handleChannelToggle('email')}
            />
          }
          label="Email (EmailJS)"
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={channels.includes('pushover')}
              onChange={() => handleChannelToggle('pushover')}
            />
          }
          label="Pushover"
        />
      </FormGroup>

      <Divider />

      {/* --- Shared: Title & Message --- */}
      <TextField
        size="small"
        label="Title / Subject"
        value={config.title || ''}
        onChange={(e) => handleChange('title', e.target.value)}
        required
        helperText="Used as email subject and Pushover title"
      />

      <TextField
        size="small"
        label="Message"
        value={config.message || ''}
        onChange={(e) => handleChange('message', e.target.value)}
        multiline
        rows={3}
        required
        helperText="The notification body content"
      />

      {/* --- Email Settings --- */}
      {channels.includes('email') && (
        <>
          <Divider />
          <Typography variant="caption" fontWeight={600}>Email Settings</Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Template</InputLabel>
            <Select
              value={config.emailTemplate || 'system_notification'}
              onChange={(e) => handleChange('emailTemplate', e.target.value)}
              label="Template"
            >
              {EMAIL_TEMPLATES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {EMAIL_TEMPLATES.find(t => t.value === (config.emailTemplate || 'system_notification'))?.description}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Notification Type</InputLabel>
            <Select
              value={config.emailType || 'info'}
              onChange={(e) => handleChange('emailType', e.target.value)}
              label="Notification Type"
            >
              {EMAIL_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
            <FormHelperText>Affects template color and icon styling</FormHelperText>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Recipients</InputLabel>
            <Select
              value={config.recipients || 'location_contacts'}
              onChange={(e) => handleChange('recipients', e.target.value)}
              label="Recipients"
            >
              <MenuItem value="location_contacts">All location contacts</MenuItem>
              <MenuItem value="manual">Manual email addresses</MenuItem>
              <MenuItem value="both">Location contacts + manual emails</MenuItem>
            </Select>
            <FormHelperText>Who should receive the email</FormHelperText>
          </FormControl>

          {/* Manual email input */}
          {(config.recipients === 'manual' || config.recipients === 'both') && (
            <Box>
              <TextField
                size="small"
                fullWidth
                label="Add email address"
                value={emailInput}
                onChange={(e) => { setEmailInput(e.target.value); setEmailError(null); }}
                onKeyDown={handleEmailKeyDown}
                error={!!emailError}
                helperText={emailError || 'Press Enter or click + to add'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleAddEmail} edge="end">
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {manualEmails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {manualEmails.map(email => (
                    <Chip
                      key={email}
                      label={email}
                      size="small"
                      onDelete={() => handleRemoveEmail(email)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {/* --- Pushover Settings --- */}
      {channels.includes('pushover') && (
        <>
          <Divider />
          <Typography variant="caption" fontWeight={600}>Pushover Settings</Typography>

          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            <Typography variant="caption">
              Requires Pushover API token and user/group key configured in system settings.
            </Typography>
          </Alert>

          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              value={config.pushoverPriority ?? 0}
              onChange={(e) => handleChange('pushoverPriority', e.target.value)}
              label="Priority"
            >
              {PUSHOVER_PRIORITIES.map(p => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Sound</InputLabel>
            <Select
              value={config.pushoverSound || 'pushover'}
              onChange={(e) => handleChange('pushoverSound', e.target.value)}
              label="Sound"
            >
              {PUSHOVER_SOUNDS.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {config.pushoverPriority === 2 && (
            <>
              <TextField
                size="small"
                label="Retry (seconds)"
                type="number"
                value={config.pushoverRetry ?? 60}
                onChange={(e) => handleChange('pushoverRetry', parseInt(e.target.value, 10) || 60)}
                helperText="How often to repeat (min 30s)"
                inputProps={{ min: 30 }}
              />
              <TextField
                size="small"
                label="Expire (seconds)"
                type="number"
                value={config.pushoverExpire ?? 3600}
                onChange={(e) => handleChange('pushoverExpire', parseInt(e.target.value, 10) || 3600)}
                helperText="Stop repeating after this many seconds (max 10800)"
                inputProps={{ min: 30, max: 10800 }}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default SendNotificationConfig;
